import React, { useCallback, useMemo, useState } from 'react';

import { ActionDropdown } from '@/components';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { ActionDialog } from '../../components/dialogs';
import { EmptyTable, ListSearch, ListShell } from '../../components/ListShell';
import {
  codeCls,
  Dash,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  PageHeader,
} from '../../components/ui';
import VendorFormModal from '../../components/VendorFormModal';
import { VendorImportModal } from '../../components/VendorImportModal';
import { useListQuery } from '../../hooks/useListQuery';
import { useLoad } from '../../hooks/useLoad';
import { useTabCounts } from '../../hooks/useTabCounts';
import { VENDOR_STATUS, VENDOR_TABS } from '../../status';
import type { Vendor, VendorStatus } from '../../types';
import { useWarehouse } from '../../WarehouseContext';

type Tab = (typeof VENDOR_TABS)[number];

const statusOf = (tab: Tab): VendorStatus | null =>
  tab === 'ALL' ? null : tab;

/**
 * Vendor master (global, not per hub): the source picked on a PO of type
 * Regular. Inactive vendors stay on old POs but can't be picked on new ones.
 */
const VendorPage: React.FC = () => {
  const { refreshToken, refresh } = useWarehouse();
  const q = useListQuery<Tab>(VENDOR_TABS, 'ALL');
  /** undefined = closed, null = create, Vendor = edit. */
  const [editing, setEditing] = useState<Vendor | null | undefined>(undefined);
  const [importing, setImporting] = useState(false);
  const [busyID, setBusyID] = useState<string | null>(null);
  /** Vendor status change waiting for its confirm. */
  const [statusTarget, setStatusTarget] = useState<{
    vendor: Vendor;
    status: VendorStatus;
  } | null>(null);

  const list = useLoad(
    (signal) =>
      warehouseApi.listVendors(
        {
          status: statusOf(q.tab),
          search: q.search.committed || null,
          page: q.page,
          limit: q.limit,
        },
        { signal }
      ),
    [q.tab, q.search.committed, q.page, q.limit, refreshToken]
  );
  const { counts } = useTabCounts(
    VENDOR_TABS,
    (k, signal) =>
      warehouseApi
        .listVendors(
          {
            status: statusOf(k),
            search: q.search.committed || null,
            limit: 1,
          },
          { signal }
        )
        .then((r) => r.pagination.total),
    [q.search.committed, refreshToken]
  );

  const tabs = VENDOR_TABS.map((k) => ({
    key: k,
    label: k === 'ALL' ? 'All' : VENDOR_STATUS[k].label,
    count: counts ? (counts[k] ?? null) : null,
  }));

  const setStatus = useCallback(
    async (v: Vendor, status: VendorStatus) => {
      setBusyID(v.id);
      try {
        await warehouseApi.updateVendor(v.id, { status });
        if (status === 'INACTIVE') {
          toast.info('Vendor dinonaktifkan', { description: v.name });
        } else {
          toast.success('Vendor diaktifkan', { description: v.name });
        }
        refresh();
      } catch (err) {
        toast.error('Gagal mengubah status vendor', {
          description: parseWarehouseError(err).message,
        });
      } finally {
        setBusyID(null);
      }
    },
    [refresh]
  );

  const offset = (q.page - 1) * q.limit;
  // The columns of ops' vendor sheet: No, ID Vendor, Name, Address, Phone.
  const columns = useMemo<TableDataColumn<Vendor>[]>(
    () => [
      {
        key: 'no',
        title: 'No',
        render: (_v, _x, i) => (
          <span className="tabular-nums text-text-sub-600">
            {offset + i + 1}
          </span>
        ),
      },
      {
        key: 'code',
        title: 'ID Vendor',
        render: (_v, x) => <span className={codeCls}>{x.code}</span>,
      },
      {
        key: 'name',
        title: 'Vendor Name',
        render: (_v, x) => <span className={nameCls}>{x.name}</span>,
      },
      {
        key: 'address',
        title: 'Vendor Address',
        render: (_v, x) =>
          x.address ? (
            <span className={`${metaCls} line-clamp-2 max-w-[320px]`}>
              {x.address}
            </span>
          ) : (
            <Dash />
          ),
      },
      {
        key: 'phone',
        title: 'Vendor Phone',
        render: (_v, x) =>
          x.phone ? <span className={nameCls}>{x.phone}</span> : <Dash />,
      },
      {
        key: 'vendorStatus',
        title: 'Status',
        render: (_v, x) => <MappedBadge map={VENDOR_STATUS} value={x.status} />,
      },
      {
        key: 'actions',
        title: '',
        align: 'right',
        render: (_v, x) =>
          busyID === x.id ? (
            <span className={metaCls}>Menyimpan…</span>
          ) : (
            <ActionDropdown
              ariaLabel={`Aksi ${x.name}`}
              actions={[
                {
                  key: 'edit',
                  label: 'Edit',
                  icon: 'ri-edit-line',
                  onClick: () => setEditing(x),
                },
                {
                  key: 'deactivate',
                  label: 'Nonaktifkan',
                  icon: 'ri-forbid-line',
                  destructive: true,
                  hidden: x.status !== 'ACTIVE',
                  onClick: () =>
                    setStatusTarget({ vendor: x, status: 'INACTIVE' }),
                },
                {
                  key: 'activate',
                  label: 'Aktifkan',
                  icon: 'ri-checkbox-circle-line',
                  hidden: x.status === 'ACTIVE',
                  onClick: () =>
                    setStatusTarget({ vendor: x, status: 'ACTIVE' }),
                },
              ]}
            />
          ),
      },
    ],
    [busyID, offset]
  );

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Supply management"
        title="Vendor"
        actions={
          <>
            <Button
              tone="neutral"
              style="stroke"
              onClick={refresh}
              leftIcon={<i className="ri-refresh-line text-[14px]" />}
            >
              Refresh
            </Button>
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setImporting(true)}
              leftIcon={<i className="ri-upload-2-line text-[14px]" />}
              data-testid="vendor-import"
            >
              Import CSV
            </Button>
            <Button
              onClick={() => setEditing(null)}
              leftIcon={<i className="ri-add-line text-[14px]" />}
              data-testid="vendor-create"
            >
              Tambah vendor
            </Button>
          </>
        }
      />
      <ListShell
        tabs={tabs}
        activeTab={q.tab}
        onTabChange={q.setTab}
        filters={
          <ListSearch
            value={q.search.value}
            onChange={q.search.setValue}
            placeholder="Cari ID, nama, atau telepon vendor…"
          />
        }
        pagination={{
          page: q.page,
          limit: q.limit,
          total: list.data?.pagination.total ?? 0,
          onPageChange: q.setPage,
          onLimitChange: q.setLimit,
        }}
      >
        {list.error ? (
          <LoadError message={list.error.message} onRetry={list.reload} />
        ) : (
          <TableData
            size="sm"
            loading={list.loading}
            data={list.data?.data ?? []}
            columns={columns}
            onRowClick={(x) => setEditing(x)}
            emptyState={<EmptyTable title="Belum ada vendor" />}
          />
        )}
      </ListShell>
      <VendorFormModal
        open={editing !== undefined}
        vendor={editing ?? null}
        onClose={() => setEditing(undefined)}
        onSaved={(v, mode) => {
          setEditing(undefined);
          toast.success(
            mode === 'create' ? 'Vendor ditambahkan' : 'Vendor disimpan',
            {
              description: `${v.code} · ${v.name}`,
            }
          );
          refresh();
        }}
      />
      <ActionDialog
        open={!!statusTarget}
        title={
          statusTarget?.status === 'INACTIVE'
            ? 'Nonaktifkan vendor'
            : 'Aktifkan vendor'
        }
        subtitle={
          statusTarget
            ? `${statusTarget.vendor.code} · ${statusTarget.vendor.name}`
            : undefined
        }
        description={
          statusTarget?.status === 'INACTIVE'
            ? 'Vendor tidak bisa dipilih di PO baru.'
            : 'Vendor bisa dipilih lagi di PO baru.'
        }
        confirmLabel={
          statusTarget?.status === 'INACTIVE' ? 'Nonaktifkan' : 'Aktifkan'
        }
        tone={statusTarget?.status === 'INACTIVE' ? 'destructive' : 'primary'}
        busy={!!statusTarget && busyID === statusTarget.vendor.id}
        testId="vendor-status-dialog"
        onClose={() => setStatusTarget(null)}
        onConfirm={() => {
          const t = statusTarget;
          if (t)
            void setStatus(t.vendor, t.status).then(() =>
              setStatusTarget(null)
            );
        }}
      />
      <VendorImportModal
        open={importing}
        onClose={() => setImporting(false)}
        onImported={(r) => {
          setImporting(false);
          toast.success(`${r.created} vendor di-import`, {
            description: r.skippedDuplicates
              ? `${r.skippedDuplicates} dilewati (sudah ada)`
              : undefined,
          });
          refresh();
        }}
      />
    </div>
  );
};

export default VendorPage;
